import { useState } from 'react'

import { Box, Button, Link } from '@factorialco/deprecated-design-system'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  BankingBankAccountFragment,
  BankingPayoutBankAccountFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Sidepanel from 'modules/core/components/Sidepanel'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useUpdateBankAccountAlias } from 'modules/banking/hooks/useUpdateBankAccountAlias'
import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
} from 'modules/banking/lib/bankAccountNumbersHelpers'
import {
  formattedBankAccountNumber,
  visibleAccountNumber,
  visibleExtraField,
} from 'modules/banking/utils'

import { SidepanelAccountingInfo } from './SidepanelAccountingInfo'
import { SidepanelDataPair } from './SidepanelDataPair'
import { SidepanelFooterAction } from './SidepanelFooterAction'
import { SidepanelMemberships } from './SidepanelMemberships'

export interface AccountDetailSidepanelProps {
  bankAccount: BankingBankAccountFragment
  loading: boolean
  disabledSaveButton: boolean
  payoutBankAccount?: BankingPayoutBankAccountFragment
  ledgerAccountId?: number
  legalEntityId: number
  legalEntityName?: string
  url?: string | null
  bankAddress?: string
  companyName?: string
  open: boolean
  hideDeleteButton?: boolean
  onboardingAuthorEmployeeId?: number
  showLedgerAccountSelector: boolean
  onClose: () => void
  onAccountingInfoChange: (newId: number) => void
  onSaveLedgerAccount: () => void
  onDeleteAccountClick: () => void
  onAddBankAccountMembershipClick: () => void
  onAddAccountMembershipClick?: () => void
  onAliasChange?: (alias: string) => void
  aliasValue?: string
  accountId?: number
}

export function AccountDetailSidepanel({
  bankAccount,
  loading,
  disabledSaveButton,
  payoutBankAccount,
  ledgerAccountId,
  legalEntityId,
  legalEntityName,
  bankAddress,
  open,
  url,
  hideDeleteButton,
  onboardingAuthorEmployeeId,
  showLedgerAccountSelector,
  onClose,
  onAccountingInfoChange,
  onSaveLedgerAccount,
  onDeleteAccountClick,
  onAddBankAccountMembershipClick,
  onAddAccountMembershipClick,
  onAliasChange,
  aliasValue,
  accountId,
}: AccountDetailSidepanelProps) {
  const extraField = visibleExtraField(bankAccount)
  const [localAliasValue, setLocalAliasValue] = useState(bankAccount.accountAlias || '')
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editInputValue, setEditInputValue] = useState('')
  const { updateBankAccountAlias } = useUpdateBankAccountAlias()

  const currentAliasValue = aliasValue || localAliasValue

  const handleEditAlias = (currentValue: string) => {
    setEditInputValue(currentValue)
    setIsEditModalOpen(true)
  }

  const handleSaveAlias = () => {
    updateBankAccountAlias({ bankAccountId: bankAccount.id, alias: editInputValue })
    if (onAliasChange) {
      onAliasChange(editInputValue)
    } else {
      setLocalAliasValue(editInputValue)
    }
    setIsEditModalOpen(false)
  }

  const handleCancelEdit = () => {
    setIsEditModalOpen(false)
  }

  const isInputEmpty = editInputValue.trim() === ''
  const hasValueChanged = editInputValue !== currentAliasValue
  const shouldDisableSaveButton = isInputEmpty || !hasValueChanged

  return (
    <>
      <Sidepanel
        width="wide"
        open={open}
        boxPadding={false}
        title={i18n.t('banking.accounts.side_panel.title')}
        onClose={onClose}
        closeLabel={i18n.t('banking.accounts.side_panel.close')}
        footer={
          <SidepanelFooterAction
            hideDeleteButton={hideDeleteButton}
            disabledSave={disabledSaveButton}
            loading={loading}
            onSave={onSaveLedgerAccount}
            onDelete={onDeleteAccountClick}
          />
        }
      >
        <Box padding="s24" gap="s16" position="relative">
          <Box flexDirection="column" justifyContent="spaceBetween" gap="s4">
            <Box flexDirection="column" paddingTop="s24" gap="s16">
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.account_alias')}
                desc={currentAliasValue ?? ''}
                editValue={currentAliasValue}
                onEdit={handleEditAlias}
                hideCopyButton
              />
              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.legal_entity')}
                desc={legalEntityName ?? ''}
                hideCopyButton
              />
              {bankAccount.beneficiaryName && (
                <SidepanelDataPair
                  title={i18n.t('banking.accounts.side_panel.beneficiary_name')}
                  desc={bankAccount.beneficiaryName}
                  hideCopyButton
                />
              )}
              <SidepanelDataPair
                title={accountNumberLabel(bankAccount.accountNumberType)}
                desc={formattedBankAccountNumber(bankAccount)}
                copyValue={visibleAccountNumber(bankAccount)}
              />
              {extraField && (
                <SidepanelDataPair
                  title={accountNumberExtraFieldLabel(bankAccount.accountNumberType) || ''}
                  desc={extraField}
                  copyValue={extraField}
                />
              )}

              <SidepanelDataPair
                title={i18n.t('banking.accounts.side_panel.currency')}
                desc={bankAccount.currency.toUpperCase()}
                hideCopyButton
              />
              {bankAddress && (
                <SidepanelDataPair
                  title={i18n.t('banking.accounts.side_panel.address')}
                  desc={bankAddress}
                />
              )}
              {payoutBankAccount && (
                <SidepanelDataPair
                  title={i18n.t('banking.accounts.side_panel.external_bank.title')}
                  info={i18n.t('banking.accounts.side_panel.external_bank.info')}
                  desc={payoutBankAccount.accountNumber.split(/(.{4})/).join(' ')}
                  desc2={payoutBankAccount.bankName}
                  copyValue={payoutBankAccount.accountNumber}
                />
              )}
              {url && (
                <Box
                  paddingTop="s12"
                  onClick={() => window.location.replace(url)}
                  style={{ cursor: 'pointer' }}
                >
                  <Link>{i18n.t('banking.accounts.side_panel.go_to_external_settings')}</Link>
                </Box>
              )}
              {showLedgerAccountSelector && (
                <SidepanelAccountingInfo
                  selectedLedgerAccountId={ledgerAccountId}
                  onAccountingInfoChange={onAccountingInfoChange}
                  legalEntityId={legalEntityId}
                  bankAccount={bankAccount}
                  disabled={loading}
                />
              )}
              <SidepanelMemberships
                bankAccount={bankAccount}
                notRemovableEmployeeIds={
                  onboardingAuthorEmployeeId ? [onboardingAuthorEmployeeId] : []
                }
                onAddClick={onAddBankAccountMembershipClick}
                onAddAccountMembershipClick={onAddAccountMembershipClick}
                accountId={accountId}
                onboardingAuthorEmployeeId={onboardingAuthorEmployeeId}
              />
              {bankAccount.lastSynchronisedAt && (
                <SidepanelDataPair
                  title={i18n.t('banking.accounts.side_panel.last_synchronised_title')}
                  desc={convertDate(
                    bankAccount.lastSynchronisedAt,
                    'backend_datetime',
                    'ui_short_with_time'
                  )}
                  hideCopyButton
                />
              )}
            </Box>
          </Box>
        </Box>
      </Sidepanel>

      {isEditModalOpen && (
        <Modal
          title={i18n.t('banking.accounts.side_panel.account_alias_modal.title')}
          onClose={handleCancelEdit}
          footer={
            <Box flexDirection="row" gap="s16">
              <Button type="secondary" onClick={handleCancelEdit}>
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={handleSaveAlias} disabled={shouldDisableSaveButton}>
                {i18n.t('common.save')}
              </Button>
            </Box>
          }
        >
          <Box gap="s16">
            <Input
              label={i18n.t('banking.accounts.side_panel.account_alias')}
              size="md"
              value={editInputValue}
              onChange={(value) => setEditInputValue(value)}
              placeholder={i18n.t('banking.accounts.side_panel.account_alias_modal.placeholder')}
            />
          </Box>
        </Modal>
      )}
    </>
  )
}
